import React, { useState } from 'react';
import { API_BASE } from '../lib/api';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';
import { TCSLogo } from '../components/TCSLogo';

const PLANS = [
  {
    id: 'bronze',
    name: 'Bronze',
    price: 'R$ 99,99',
    color: '#00F0FF',
    tag: 'Principal',
  },
  {
    id: 'prata',
    name: 'Prata',
    price: 'R$ 200,00',
    color: '#C0C7D1',
  },
  {
    id: 'ouro',
    name: 'Ouro',
    price: 'R$ 300,00',
    color: '#FFB800',
  },
];

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [plan, setPlan] = useState('bronze');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptTerms) {
      setError('Você precisa aceitar os termos e aguardar aprovação do treinador');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/auth/register`,
        {
          name,
          email,
          password,
          plan,
          accept_terms: acceptTerms,
        },
        { withCredentials: true }
      );
      setRegistered(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/2165cc64cd0fa469bc14e5db51b7bfd3536e050111542ec559eaebfb3a9231b4.png)' }}
        >
          <div className="absolute inset-0 bg-black/80"></div>
        </div>
        <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-lg bg-black/60 backdrop-blur-xl border border-[#00F0FF]/30 p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#00F0FF]/10 border-2 border-[#00F0FF] flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#00F0FF]" />
            </div>
            <h1 className="text-3xl font-black uppercase tracking-tighter text-[#00F0FF] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Cadastro Recebido!
            </h1>
            <p className="text-white mb-4">Seu cadastro foi realizado com sucesso.</p>
            <div className="bg-[#FFB800]/10 border border-[#FFB800] p-4 mb-6">
              <p className="text-[#FFB800] font-semibold mb-2">Aguardando Aprovação</p>
              <p className="text-white text-sm">
                Seu cadastro será analisado pelo treinador <strong>Thyago Costa Silva</strong>. Você receberá acesso assim que aprovado.
              </p>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none h-12"
              data-testid="back-to-login-btn"
            >
              Voltar ao Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/2165cc64cd0fa469bc14e5db51b7bfd3536e050111542ec559eaebfb3a9231b4.png)' }}
      >
        <div className="absolute inset-0 bg-black/80"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-8">
            <div className="flex flex-col items-center mb-6">
              <TCSLogo size="md" showTagline={true} />
            </div>

            <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-2 text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Cadastro de Atleta
            </h2>
            <p className="text-xs text-[#A1A1AA] mb-6 text-center">
              Rápido e simples. Dados complementares podem ser preenchidos depois da aprovação.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-[#FF3366]/10 border border-[#FF3366]/50 text-[#FF3366] text-sm" data-testid="register-error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Nome Completo</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-12"
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-12"
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Senha</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-12 pr-12"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors"
                    data-testid="register-toggle-password"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-[#A1A1AA] mt-1">Mínimo de 6 caracteres</p>
              </div>

              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Escolha seu Plano</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="register-plan-selector">
                  {PLANS.map((p) => {
                    const selected = plan === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlan(p.id)}
                        className={`relative text-left p-3 border transition-all rounded-none focus:outline-none ${
                          selected ? 'bg-black/80' : 'bg-[#121212] border-white/10 hover:border-white/30'
                        }`}
                        style={selected ? { borderColor: p.color, boxShadow: `0 0 0 1px ${p.color}` } : {}}
                        data-testid={`register-plan-${p.id}`}
                        aria-pressed={selected}
                      >
                        {p.tag && (
                          <span
                            className="absolute -top-2 left-2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                            style={{ backgroundColor: p.color, color: '#000' }}
                          >
                            {p.tag}
                          </span>
                        )}
                        <p
                          className="text-sm font-black uppercase tracking-tight"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif', color: selected ? p.color : '#fff' }}
                        >
                          {p.name}
                        </p>
                        <p className="text-xs text-[#A1A1AA] mt-1">{p.price}</p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#A1A1AA] mt-2">
                  Você pode trocar de plano mais tarde com o treinador.
                </p>
              </div>


              <div className="bg-[#121212] border border-white/10 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    className="mt-1 border-[#00F0FF] data-[state=checked]:bg-[#00F0FF] data-[state=checked]:text-black rounded-none"
                    data-testid="accept-terms-checkbox"
                  />
                  <span className="text-sm text-white">
                    Concordo que meu cadastro será <strong className="text-[#00F0FF]">analisado e aprovado</strong> pelo treinador <strong>Thyago Costa Silva</strong>. Entendo que só terei acesso à plataforma após aprovação.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading || !acceptTerms}
                className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase tracking-wide h-12 rounded-none transition-all disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? 'Enviando...' : 'Solicitar Cadastro'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[#A1A1AA] text-sm">
                Já tem uma conta?{' '}
                <Link to="/login" className="text-[#00F0FF] hover:text-[#4DFFFF] font-bold" data-testid="register-login-link">
                  Fazer Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
